import React, { Component } from 'react';
import { arrayOf, bool, object, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Switch, Route, withRouter, useLocation } from 'react-router-dom';
import { NotFoundPage } from './containers';
import { NamedRedirect, LoadableComponentErrorBoundary } from './components';
import { locationChanged } from './ducks/Routing.duck';
import { propTypes } from './util/types';
import * as log from './util/log';
import { parse, stringify } from './util/urlHelpers'
import { canonicalRoutePath } from './util/routes';
import routeConfiguration from './routeConfiguration';
import queryString from 'query-string';
import StripeLoader, {pagesUsingStripe} from './util/stripeLoader';
import { config } from 'dotenv';

const canShowComponent = props => {
  const { isAuthenticated, route } = props;
  const { auth } = route;
  return !auth || isAuthenticated;
};

const needsStripePage = props => {
  const { route} = props;
  const { name: pageName } = route;
  //console.log(`[** Routes **]: Checking if ${pageName} needs stripe`)
  return pagesUsingStripe.includes(pageName);
}


const callLoadData = props => {
  const { match, location, route, dispatch, logoutInProgress } = props;
  const { loadData, name } = route;
  const shouldLoadData =
    typeof loadData === 'function' && canShowComponent(props) && !logoutInProgress;

  if (shouldLoadData) {
    dispatch(loadData(match.params, location.search))
      .then(() => {
        // eslint-disable-next-line no-console
        //console.warn(`loadData success for ${name} route`);
      })
      .catch(e => {
        log.error(e, 'load-data-failed', { routeName: name });
      });
  }
};

const setPageScrollPosition = location => {
  if (!location.hash) {
    // No hash, scroll to top
    window.scroll({
      top: 0,
      left: 0,
    });
  } else {
    const el = document.querySelector(location.hash);
    if (el) {
      // Found element from the current page with the given fragment identifier,
      // scrolling to that element.
      //
      // NOTE: This only works on in-app navigation within the same page.
      // If smooth scrolling is needed between different pages, one needs to wait
      //   1. loadData fetch and
      //   2. code-chunk fetch
      // before making el.scrollIntoView call.

      el.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }
  }
};

const handleLocationChanged = (dispatch, location, AffiliateRef, utm_parameters, history) => {
  setPageScrollPosition(location);

  const parsedSearch = parse(location.search)
  let currentAffiliateRef = AffiliateRef
  let newAffiliateRef = parsedSearch.ref
  let currentUtmParameters = utm_parameters

  // if(location.search === '' && currentAffiliateRef === null){
  //   // do nothing
  //   return  
  // } else 


  // the search query is empty and the affilate ref is defined,
  // so the ref query paramter is added to the parsed query object 
  if(location.search === '' && currentAffiliateRef !== null){
    // add referer to parsed query param object
    parsedSearch.ref = currentAffiliateRef
  }
  // the search query contains query parameters and the affilate ref is not defined.
  else if(location.search !== '' && currentAffiliateRef === null){
    // check if the parsed query object has the affiliate ref property 
    // and then update the currentAffiliate variable that stores the ref
    if (parsedSearch.hasOwnProperty('ref')){
      currentAffiliateRef = newAffiliateRef
    }      
  }
  // the search query contains query parameters and the affilate ref is defined.
  else if(location.search !== '' && currentAffiliateRef !== null){
    // check if the parsed query strings object has a ref property
    if (parsedSearch.hasOwnProperty('ref')){
      // if affiliate in the redux store and ref in the query param is the same then no changes are necessary.
      // if different then we update the ref and currentAffiliateRef.
      // This covers the case when scanning a new affiliate link 
      if(parsedSearch.ref !== currentAffiliateRef ){
        currentAffiliateRef = newAffiliateRef
      } 
    } else {
      // add current ref to the parsed query parameters
      parsedSearch.ref = currentAffiliateRef
    }
  }

  // if currentUtmParameters is empty, then check if utm params exist in the search query
  // and then add them to the current Utm parameters object which is stored in the redux store
  if( Object.keys(currentUtmParameters).length === 0 ){
    Object.entries(parsedSearch).forEach(([key, value]) => {
      if(key.startsWith('utm_')){
        currentUtmParameters[key] = value
      }
    });
  }

  // to form a valid url with the affiliate referral and utm parameters,
  // these two objects need to be merged to form a single object that is then stringified
  const mergeURLQueryParam = {...parsedSearch, ...currentUtmParameters}
  location.search = stringify(mergeURLQueryParam)

  const url = canonicalRoutePath(routeConfiguration(), location);
  dispatch(locationChanged(location, url, currentAffiliateRef, currentUtmParameters));

  history.replace({
    search: location.search
  })
};

/**
 * RouteComponentRenderer handles loadData calls on client-side.
 * It also checks authentication and redirects unauthenticated users
 * away from routes that are for authenticated users only
 * (aka "auth: true" is set in routeConfiguration.js)
 *
 * This component is a container: it needs to be connected to Redux.
 */
class RouteComponentRenderer extends Component {
  constructor(props){
    super(props)
    this.state = {
      updadeRefState: false,
      stripeLoaded: false
    }
    this.onStripeLoaded = this.onStripeLoaded.bind(this);
  }

  onStripeLoaded() {
    //console.log("[** Routes **]: onStripeLoaded && stripeLoaded=", this.state.stripeLoaded)
    const needsStripe = needsStripePage(this.props);
    if (needsStripe) {
      //console.log("[** Routes **]:  => needs Stripe... will update loadingStripe and re-render");
      this.setState({
        stripeLoaded: true
      })
    }
  }

  componentDidMount() {
    // Calling loadData on initial rendering (on client side).
    callLoadData(this.props);
    handleLocationChanged(this.props.dispatch, this.props.location, this.props.currentAffiliateRef, this.props.utm_parameters, this.props.history);
    this.setState({
      updadeRefState: true
    });
    //console.log("[** Routes **]: componentDidMount && stripeLoaded=", this.state.stripeLoaded)
  }

  componentDidUpdate(prevProps, prevState) {
    // Call for handleLocationChanged affects store/state
    // and it generates an unnecessary update.
    
    // Note: creating URLSearchParams avoids having to deal with 
    // differences where searches optionally start with '?'
    const previousSearch = new URLSearchParams(prevProps.location.search);
    const newSearch = new URLSearchParams(this.props.location.search);
    
    if (previousSearch.toString() !== newSearch.toString()
    || prevProps.location.hash !== this.props.location.hash
    || prevProps.location.pathname !== this.props.location.pathname) {
      // Calling loadData after initial rendering (on client side).
      // This makes it possible to use loadData as default client side data loading technique.
      // However it is better to fetch data before location change to avoid "Loading data" state.
      callLoadData(this.props);
      if (!this.state.updadeRefState) {
        handleLocationChanged(
          this.props.dispatch,
          this.props.location,
          this.props.currentAffiliateRef,
          this.props.utm_parameters,
          this.props.history
        );
      }
    }
  }

  componentWillUnmount() {
    this.setState({
      updadeRefState: false
    })
  }

  render() {
    const { route, match, location, staticContext } = this.props;
    const { component: RouteComponent, authPage = 'SignupPage', extraProps } = route;
    const canShow = canShowComponent(this.props);
    //console.log("[** Routes **]: render && stripeLoaded=", this.state.stripeLoaded)
    const needsStripe = needsStripePage(this.props);

    if (!canShow) {
      staticContext.unauthorized = true;
    }
    return canShow ? (
      <LoadableComponentErrorBoundary>
        {needsStripe && !this.state.stripeLoaded ?
        <StripeLoader onStripeLoaded={this.onStripeLoaded}/> 
        :
        <RouteComponent params={match.params} location={location} {...extraProps} />
        }
      </LoadableComponentErrorBoundary>
    ) : (
      <NamedRedirect
        name={authPage}
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }
}

RouteComponentRenderer.defaultProps = { staticContext: {} };

RouteComponentRenderer.propTypes = {
  isAuthenticated: bool.isRequired,
  logoutInProgress: bool.isRequired,
  route: propTypes.route.isRequired,
  match: shape({
    params: object.isRequired,
    url: string.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,
  staticContext: object,
  dispatch: func.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated, logoutInProgress } = state.Auth;
  const { currentAffiliateRef, utm_parameters } = state.Routing;
  return { isAuthenticated, logoutInProgress, currentAffiliateRef, utm_parameters };
};
const RouteComponentContainer = compose(connect(mapStateToProps))(RouteComponentRenderer);

/**
 * Routes component creates React Router rendering setup.
 * It needs routeConfiguration (named as "routes") through props.
 * Using that configuration it creates navigation on top of page-level
 * components. Essentially, it's something like:
 * <Switch>
 *   <Route render={pageA} />
 *   <Route render={pageB} />
 * </Switch>
 */
const Routes = (props, context) => {
  const { isAuthenticated, logoutInProgress, routes, history } = props;

  const toRouteComponent = route => {
    const renderProps = {
      isAuthenticated,
      logoutInProgress,
      route,
    };

    // By default, our routes are exact.
    // https://reacttraining.com/react-router/web/api/Route/exact-bool
    const isExact = route.exact != null ? route.exact : true;
    return (
      <Route
        key={route.name}
        path={route.path}
        exact={isExact}
        render={matchProps => (
          <RouteComponentContainer
            {...renderProps}
            match={matchProps.match}
            location={matchProps.location}
            staticContext={matchProps.staticContext}
            history={history}
          />
        )}
      />
    );
  };

  // N.B. routes prop within React Router needs to stay the same,
  // so that React is is not rerendering page component.
  // That's why we pass-in props.routes instead of calling routeConfiguration here.
  return (
    <Switch>
      {routes.map(toRouteComponent)}
      <Route component={NotFoundPage} />
    </Switch>
  );
};

Routes.propTypes = {
  routes: arrayOf(propTypes.route).isRequired,
};

export default withRouter(Routes);
